import { type MutableRefObject, useCallback, useEffect, useRef } from "react";

import { zodResolver } from "@hookform/resolvers/zod";
import { Button, type ExtractRefType, Icon_Checkmark24, Menu, Modal } from "@shapr3d/frontend-lib";
import { useForm, useWatch } from "react-hook-form";
import { z } from "zod";

import type {
  ProjectInvitation,
  ProjectSharingBaseRole,
} from "../../../../../hooks/useProjectSharing";
import type {
  InvitationID,
  ProjectViewLinkInvitation,
} from "../../../../../hooks/useProjectViewLink";
import type { WebExportBaseRole } from "../../../../../hooks/useWebExport";
import { Icon_FillerSvg24 } from "./FillerSvg";
import { MenuPortal } from "./MenuPortal";
import { SelectActivator } from "./SelectActivator";
import { SingleIconLabel } from "./SingleIconLable";

import style from "./InvitationRow.module.css";

interface InvitationRowProps {
  invitation: ProjectViewLinkInvitation | ProjectInvitation;
  containerIntersectionObserver?: IntersectionObserver | null;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  schema: z.ZodObject<any>;
  isError?: boolean;
  deleteConfirmationOpen?: boolean;
  onUpdate: ({
    baseRole,
  }: {
    baseRole: ProjectSharingBaseRole | WebExportBaseRole;
  }) => Promise<ProjectViewLinkInvitation | void>;
  onDelete: () => Promise<InvitationID | void>;
  onRemoveIntended?: () => void;
  isPending?: boolean;
  onOpenDeleteConfirmation?: () => void;
  onCloseDeleteConfirmation?: () => void;
  animationPromiseResolver?: MutableRefObject<(() => void) | null>;
}

const baseRoleOptions: { [key in WebExportBaseRole | ProjectSharingBaseRole]: string } = {
  viewer: "Viewer",
  commenter: "Commenter",
} as const;

type AllBaseRoles = keyof typeof baseRoleOptions;

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function getValidBaseRolesFromSchemaObject(schema: z.ZodObject<any>): AllBaseRoles[] {
  const baseRoleField = schema.shape.baseRole;
  if (baseRoleField instanceof z.ZodEnum) {
    return baseRoleField.options as AllBaseRoles[];
  }
  if (baseRoleField instanceof z.ZodUnion) {
    const literals = baseRoleField._def.options
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      .filter((opt: any): opt is z.ZodLiteral<AllBaseRoles> => opt instanceof z.ZodLiteral)
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      .map((lit: any) => lit.value);
    return literals;
  }
  return [];
}
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function getRoleOptionsForSchema(schema: z.ZodObject<any>): Record<AllBaseRoles, string> {
  const validRoles = getValidBaseRolesFromSchemaObject(schema);
  return Object.fromEntries(validRoles.map((role) => [role, baseRoleOptions[role]])) as Record<
    AllBaseRoles,
    string
  >;
}

export const InvitationRow = ({
  invitation,
  containerIntersectionObserver,
  schema,
  isPending,
  isError,
  deleteConfirmationOpen = false,
  onUpdate,
  onDelete,
  onRemoveIntended,
  onOpenDeleteConfirmation,
  onCloseDeleteConfirmation,
  animationPromiseResolver,
}: InvitationRowProps) => {
  const formRef = useRef<HTMLFormElement>(null);
  const menuRef = useRef<ExtractRefType<typeof MenuPortal>>(null);

  const roleOptions = getRoleOptionsForSchema(schema);

  useEffect(() => {
    if (formRef.current) {
      containerIntersectionObserver?.observe(formRef.current);
    }
    return () => containerIntersectionObserver?.disconnect();
  }, [containerIntersectionObserver]);

  const { control, setValue, handleSubmit, setError, reset } = useForm({
    mode: "onChange",
    defaultValues: {
      baseRole: invitation.baseRole,
    },
    resolver: zodResolver(schema),
  });
  const baseRoleWatch = useWatch({ name: "baseRole", control });

  const resetForm = useCallback(() => {
    reset(
      {
        baseRole: invitation.baseRole,
      },
      { keepErrors: true }
    );
  }, [reset, invitation.baseRole]);

  const onSubmit = async (data: { baseRole: ProjectSharingBaseRole | WebExportBaseRole }) => {
    try {
      await onUpdate?.(data);
    } catch {
      resetForm();
      setError("baseRole", {
        type: "manual",
        message: "Something went wrong. Please try again.",
      });
    }
  };

  return (
    <>
      <div>
        <form ref={formRef} onSubmit={handleSubmit(onSubmit)} className={style.row}>
          <div className={style.email}>{invitation.email}</div>
          <div className={style.pill}>pending</div>

          <div className={style.roleContainer}>
            <MenuPortal
              dropdownPosition="left"
              ref={menuRef}
              activator={
                <SelectActivator disabled={isPending} label={baseRoleOptions[baseRoleWatch]} />
              }
            >
              {Object.entries(roleOptions).map(([baseRole, label]) => (
                <MenuPortal.Item
                  key={baseRole}
                  closeOnClick
                  onClick={(event) => {
                    event.preventDefault();
                    if (baseRole === baseRoleWatch) {
                      return;
                    }
                    setValue("baseRole", baseRole);
                    formRef.current?.requestSubmit();
                  }}
                >
                  <SingleIconLabel
                    icon={baseRole === baseRoleWatch ? <Icon_Checkmark24 /> : <Icon_FillerSvg24 />}
                    label={label}
                  />
                </MenuPortal.Item>
              ))}
              <Menu.Divider />
              <Menu.Item
                closeOnClick
                onClick={async (event) => {
                  event.preventDefault();
                  onRemoveIntended?.();
                  onOpenDeleteConfirmation?.();
                }}
              >
                <span className={style.red}>Remove...</span>
              </Menu.Item>
            </MenuPortal>
          </div>
        </form>
        {isError && <span className={style.red}>{"Something went wrong. Please try again."}</span>}
      </div>
      <Modal
        size="small"
        title="Remove invitation?"
        open={deleteConfirmationOpen}
        onClose={() => {
          if (animationPromiseResolver?.current) {
            animationPromiseResolver.current();
            animationPromiseResolver.current = null;
          }
          onCloseDeleteConfirmation?.();
        }}
      >
        <div className={style.deleteConfirmationContainer}>
          <span>{`This invitation will be removed immediately. You can\u2019t undo this action.`}</span>
          <Button
            variant={"red"}
            disabled={isPending}
            onClick={async () => {
              try {
                await onDelete();
              } catch {
                // This is not to throw unhandled promise rejection error
                onCloseDeleteConfirmation?.();
              }
            }}
          >
            Remove
          </Button>
        </div>
      </Modal>
    </>
  );
};
